// The bankroll over the chart's window, layered: what is not in play at the bottom, then each live
// call's value on top. Each past moment is the SDK's own valuation of the calls held now at the
// prices of that moment (candle closes, then every mark this page saw), so a layer moves exactly as
// its payout would have. Before a call opened, its stake sits in the bottom layer. Numbers here are
// for drawing; the figures shown in words are the live valuation's.

import { useEffect, useMemo, useState } from "react";
import { useQueries } from "@tanstack/react-query";
import { pt, type Position } from "../../sdk";
import { candles } from "../../hl/info";
import { big } from "../../lib/num";
import { useAccount, useOwner } from "../../state/account";
import { useInstruments } from "../../state/market";
import { usePref } from "../../state/prefs";
import { ticksOf, type Tick } from "../../state/ticks";
import { RANGES } from "./LiveChart";

export const BUCKETS = 96;
const REFRESH_MS = 2_000;
const INTERVAL_MS: Record<string, number> = { "1m": 60_000, "5m": 300_000, "15m": 900_000 };

export type Layer = {
  key: string;
  /** The positions this layer stands for: one, or all of them. */
  ids: string[];
  isLong: boolean | null;
  symbol: string;
  openedAt: number;
  /** Dollars at each bucket; 0 before the call opened. */
  values: number[];
};

export type BankrollSeries = {
  start: number;
  span: number;
  times: number[];
  /** Dollars not in play at each bucket. */
  idle: number[];
  layers: Layer[];
};

/** Raw collateral (18 decimals) as dollars, for drawing. */
export const dollarsOf = (raw: string | bigint) => Number(big(typeof raw === "bigint" ? raw.toString() : raw) / 10n ** 12n) / 1_000_000;

function priceAt(points: Tick[], t: number): number | null {
  if (points.length === 0) return null;
  let lo = 0;
  let hi = points.length - 1;
  if ((points[0] as Tick).t > t) return (points[0] as Tick).p;
  while (lo < hi) {
    const mid = (lo + hi + 1) >> 1;
    if ((points[mid] as Tick).t <= t) lo = mid;
    else hi = mid - 1;
  }
  return (points[lo] as Tick).p;
}

/** A price as the integer the SDK reads at `scale` (10 is one place): for drawing a past moment. */
const rawAt = (price: number, scale: string) => Math.round(price * Number(scale)).toString();

export function useBankrollSeries(): BankrollSeries | null {
  const owner = useOwner();
  const account = useAccount();
  const instruments = useInstruments();
  const rangeValue = usePref("range");
  const range = RANGES.find((item) => item.value === rangeValue) ?? RANGES[1];
  const positions = useMemo(() => account?.positions ?? [], [account?.positions]);
  const held = useMemo(
    () => instruments.filter((instrument) => positions.some((position) => position.instrumentId === instrument.id)),
    [instruments, positions],
  );
  const histories = useQueries({
    queries: held.map((instrument) => ({
      queryKey: ["chartHistory", instrument.symbol, range.interval, range.count],
      queryFn: ({ signal }: { signal: AbortSignal }) => candles(instrument.symbol, range.interval, range.count, signal),
      refetchInterval: INTERVAL_MS[range.interval] ?? 60_000,
      staleTime: 15_000,
    })),
  });
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const timer = setInterval(() => setTick((n) => n + 1), REFRESH_MS);
    return () => clearInterval(timer);
  }, []);

  const historyKey = histories.map((query) => query.dataUpdatedAt).join(",");
  const positionKey = positions.map((position) => position.positionId).join(",");

  return useMemo(() => {
    if (!owner || positions.length === 0 || held.length === 0) return null;
    const now = Date.now();
    const span = range.ms;
    const start = now - span;
    const step = INTERVAL_MS[range.interval] ?? 60_000;

    // each held market's prices: candle closes up to the first live tick, then the ticks
    const series = new Map<number, Tick[]>();
    held.forEach((instrument, index) => {
      const ticks = ticksOf(instrument.id);
      const firstLive = ticks[0]?.t ?? Infinity;
      const closes = (histories[index]?.data ?? [])
        .map((candle) => ({ t: Math.min(candle.t + step, now), p: Number(candle.c) }))
        .filter((point) => point.t < firstLive && point.p > 0);
      series.set(instrument.id, [...closes, ...ticks]);
    });

    const times = Array.from({ length: BUCKETS }, (_, k) => start + (k / (BUCKETS - 1)) * span);
    const values = new Map<string, number[]>(positions.map((position) => [position.positionId, []]));
    const valid: boolean[] = [];
    for (const t of times) {
      const marks: Record<string, string> = {};
      let complete = true;
      for (const instrument of held) {
        const p = priceAt(series.get(instrument.id) ?? [], t);
        if (p === null) complete = false;
        else marks[String(instrument.id)] = rawAt(p, instrument.priceScale);
      }
      valid.push(complete);
      let valued: ReturnType<typeof pt.valuations> = null;
      try {
        valued = complete ? pt.valuations(owner, marks) : null;
      } catch {
        valued = null;
      }
      const byId = new Map((valued?.positions ?? []).map((valuation) => [valuation.positionId, valuation]));
      for (const position of positions) {
        const valuation = byId.get(position.positionId);
        const open = t >= position.openedAtMs;
        values.get(position.positionId)?.push(open && valuation ? dollarsOf(valuation.payout) : open ? dollarsOf(position.margin) : 0);
      }
    }
    if (!valid.some(Boolean)) return null;

    const ordered = [...positions].sort((a, b) => a.openedAtMs - b.openedAtMs);
    const symbolOf = (position: Position) => instruments.find((instrument) => instrument.id === position.instrumentId)?.symbol ?? "";
    const layers: Layer[] = ordered.map((position) => ({
      key: position.positionId,
      ids: [position.positionId],
      isLong: position.isLong,
      symbol: symbolOf(position),
      openedAt: position.openedAtMs,
      values: values.get(position.positionId) ?? [],
    }));

    // what was not in play: the stakes of calls opened later were still in the bankroll then
    const idle = times.map((t) => ordered.filter((position) => position.openedAtMs > t).reduce((sum, position) => sum + dollarsOf(position.margin), 0));
    return { start, span, times, idle, layers };
    // tick re-reads the marks seen since; the keys say when the calls or prices moved
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [owner, positionKey, historyKey, range.value, tick, held]);
}
